/**
 * This dialog component confirms whether the user really wants to delete a given user account.
 * All visible text is in German, per request.
 *
 * Props:
 *  - handleClose {Function}: Closes the dialog
 *  - dialog {Object}: Contains data for the user that will be deleted
 */

import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { setDialog } from "src/features/dialog/dialogSlice";
import Iconify from "src/components/iconify";
import { useDeleteUserMutation } from "src/features/users/usersApiSlice";

function DeleteUserDialog({ handleClose, dialog }) {
  const [deleteUser] = useDeleteUserMutation();
  const dispatch = useDispatch();

  const handleDeleteUser = async () => {
    try {
      // Call the deleteUser mutation with the ID of the user to be deleted
      await deleteUser({ userId: dialog?.data?._id }).unwrap();

      // Show a success dialog
      dispatch(
        setDialog({
          title: "Benutzer wurde erfolgreich gelöscht!",
          type: "SUCCESS",
          data: {
            successText:
              "Der Benutzer und alle zugehörigen Daten wurden entfernt.",
          },
        })
      );
    } catch (error) {
      console.error("Error deleting user:", error);

      // Show an error dialog
      dispatch(
        setDialog({
          title: "Fehler beim Löschen",
          type: "ERROR",
          data: {
            errorText: "Es gab ein Problem beim Löschen des Benutzers.",
          },
        })
      );
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Typography
          sx={{ textAlign: "center", fontSize: "30px", flex: 1 }}
          id="customized-dialog-title"
        >
          Benutzer löschen
        </Typography>
        <Box sx={{ flex: "0 0 auto" }}>
          <Iconify
            onClick={handleClose}
            icon="mingcute:close-line"
            style={{ fontSize: "50px", color: "action.active" }}
            sx={{
              transition: "transform 0.1s",
              "&:hover": {
                transform: "scale(1.8)",
              },
            }}
          />
        </Box>
      </Box>

      <DialogContent dividers>
        <Typography variant="body1">
          Möchten Sie diesen Benutzer wirklich löschen?
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Abbrechen
        </Button>
        <Button onClick={handleDeleteUser} variant="contained" color="error">
          Löschen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteUserDialog;
